import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { credentialsApi, type CredentialDetail } from '@/api/credentials';
import { errorMessage } from '@/api/client';
import { tenantsApi } from '@/api/tenants';
import { base64ToBytes, bytesToBase64, decryptCredential, encryptCredentialForShares, unwrapKey, wrapKey } from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';
import { confirmDialog } from '@/stores/dialog';

// ==========================================================
// SharesSection — partajare + revocare
// ==========================================================

interface SharesSectionProps {
  cred: CredentialDetail;
  keyPair: { publicKey: Uint8Array; privateKey: Uint8Array };
  tenantId: string;
}

export function SharesSection({ cred, keyPair, tenantId }: SharesSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [permission, setPermission] = useState<'read' | 'write'>('read');
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);

  const membersQuery = useQuery({
    queryKey: ['tenant-members', tenantId],
    queryFn: () => tenantsApi.listMembers(tenantId),
    enabled: expanded && !!tenantId,
  });

  const sharesQuery = useQuery({
    queryKey: ['credential-shares', cred.id],
    queryFn: () => credentialsApi.listShares(cred.id),
    enabled: expanded,
  });

  const addMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      setError(null);
      const target = membersQuery.data?.find((m) => m.user_id === targetUserId);
      if (!target) throw new Error(t('vault_ui.detail.unknown_recipient'));

      // Unwrap DEK-ul nostru (din dek_wrapped pe detail) → re-wrap pentru public_key-ul
      // destinatarului. Totul local.
      const dek = await unwrapKey(base64ToBytes(cred.dek_wrapped), keyPair);
      const targetPubKey = base64ToBytes(target.public_key);
      const newWrapped = await wrapKey(dek, targetPubKey).finally(() => dek.fill(0));
      return credentialsApi.addShare(cred.id, {
        user_id: targetUserId,
        dek_wrapped: bytesToBase64(newWrapped),
        permission,
      });
    },
    onSuccess: () => {
      setSelectedUserId('');
      setPermission('read');
      queryClient.invalidateQueries({ queryKey: ['credential-shares', cred.id] });
    },
    onError: (err) => {
      setError(errorMessage(err, t('vault_ui.detail.share_error')));
    },
  });

  // Revoke + rotate: decrypt cu DEK vechi, generează DEK nou, re-encrypt,
  // wrap pt remaining (fără revoked), POST /rotate cu revoke=[userId].
  // Zero-acces post-revoke: userul revocat nu mai poate decripta viitor.
  const rotateRevokeMutation = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      if (!sharesQuery.data || !membersQuery.data) {
        throw new Error(t('vault_ui.detail.no_shares_members'));
      }
      // 1. Decriptează plaintext-ul actual cu DEK vechi (via dek_wrapped pt caller)
      const payload = await decryptCredential(
        {
          ciphertext: cred.ciphertext,
          nonce: cred.nonce,
          dek_wrapped: cred.dek_wrapped,
        },
        keyPair,
      );
      // 2. Construiește lista remaining (toți userii cu share, fără cel revocat)
      const remaining = sharesQuery.data.filter((s) => s.user_id !== userId);
      const recipients = remaining.map((s) => {
        const m = membersQuery.data!.find((mm) => mm.user_id === s.user_id);
        if (!m) {
          throw new Error(
            t('vault_ui.detail.share_without_member', { userId: s.user_id }),
          );
        }
        return {
          userId: s.user_id,
          publicKey: base64ToBytes(m.public_key),
          isSelf: s.user_id === currentUserId,
        };
      });
      if (recipients.length === 0) {
        throw new Error(t('vault_ui.detail.cannot_revoke_last'));
      }
      // 3. Re-encrypt cu DEK nou + wrap pt remaining
      const encrypted = await encryptCredentialForShares(payload, recipients);
      return credentialsApi.rotate(cred.id, {
        ciphertext: encrypted.ciphertext,
        nonce: encrypted.nonce,
        dek_wraps: encrypted.dek_wraps,
        revoke: [userId],
        change_reason: 'revoke + rotate DEK',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential-shares', cred.id] });
      queryClient.invalidateQueries({ queryKey: ['credential', cred.id] });
    },
    onError: (err) => {
      setError(errorMessage(err, t('vault_ui.detail.rotate_error')));
    },
  });

  // Userii ne-partajați încă, excluzându-l și pe user-ul curent care e deja owner
  const availableTargets =
    membersQuery.data?.filter(
      (m) =>
        m.user_id !== currentUserId &&
        !sharesQuery.data?.some((s) => s.user_id === m.user_id),
    ) ?? [];

  return (
    <section className="pt-4 border-t border-[var(--border)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 text-xs uppercase text-[var(--text-muted)] font-semibold hover:text-[var(--text)] dark:hover:text-[var(--text)]"
      >
        <span>{expanded ? '▼' : '▶'}</span>
        {t('vault_ui.detail.sharing_title')}
        {sharesQuery.data && (
          <span className="text-[10px] normal-case text-[var(--text-dim)]">
            ({sharesQuery.data.length})
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {sharesQuery.isLoading && (
            <p className="text-xs text-[var(--text-muted)]">{t('common.loading')}</p>
          )}
          {sharesQuery.data && (
            <div className="space-y-1">
              {sharesQuery.data.map((s) => (
                <div
                  key={s.user_id}
                  className="flex items-center justify-between text-sm border border-[var(--border)] rounded px-3 py-2"
                >
                  <div>
                    <span className="font-medium">{s.email}</span>
                    <span className="ml-2 badge bg-[var(--bg-surface)] text-[var(--text-muted)] text-[10px]">
                      {s.permission}
                    </span>
                  </div>
                  {s.permission !== 'owner' && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50 font-medium"
                        onClick={async () => {
                          if (
                            await confirmDialog(
                              t('vault_ui.detail.revoke_rotate_confirm', { email: s.email }),
                            )
                          ) {
                            rotateRevokeMutation.mutate(s.user_id);
                          }
                        }}
                        disabled={rotateRevokeMutation.isPending}
                        title={t('vault_ui.detail.revoke_rotate_title')}
                      >
                        {rotateRevokeMutation.isPending ? t('vault_ui.detail.rotating') : t('vault_ui.detail.revoke_rotate')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {availableTargets.length > 0 ? (
            <div className="flex gap-2">
              <select
                className="input text-sm"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={addMutation.isPending}
              >
                <option value="">{t('vault_ui.detail.choose_member')}</option>
                {availableTargets.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.email} ({m.role})
                  </option>
                ))}
              </select>
              <select
                className="input text-sm w-auto"
                aria-label={t('sharing.permission')}
                value={permission}
                onChange={(e) => setPermission(e.target.value as 'read' | 'write')}
                disabled={addMutation.isPending}
              >
                <option value="read">{t('sharing.read')}</option>
                <option value="write">{t('sharing.write')}</option>
              </select>
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => selectedUserId && addMutation.mutate(selectedUserId)}
                disabled={!selectedUserId || addMutation.isPending}
              >
                {addMutation.isPending ? t('vault_ui.detail.sharing_busy') : t('vault_ui.detail.share')}
              </button>
            </div>
          ) : membersQuery.data && membersQuery.data.length <= 1 ? (
            <p className="text-xs text-[var(--text-muted)]">
              {t('vault_ui.detail.no_other_members')}
            </p>
          ) : (
            <p className="text-xs text-[var(--text-muted)]">
              {t('vault_ui.detail.shared_with_all')}
            </p>
          )}

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          <p className="text-[10px] text-[var(--text-dim)] italic">
            {t('vault_ui.detail.sharing_note')}
          </p>
        </div>
      )}
    </section>
  );
}
